// Documentos con formato XmarteK a partir de lo que escribe Amix en Markdown (D91, D93): Word (.docx real, armado aqui
// sin librerias) con el formato de las propuestas 2026 (logo en el encabezado, Arial, tablas azul 1F4E5F con filas
// EAF1F4, pie con las dos sedes y numero de pagina) y la misma pagina en HTML para imprimir. El PDF de verdad lo hace
// el servidor a partir de este mismo .docx (asistente /pdf, D93): un solo diseno. Funciones puras salvo las de descarga.
import { type BloqueIA, bloquesIA } from './markdownIA';
import { zip } from './zip';

export { crc32, zip } from './zip';

export const MARCA = {
  nombre: 'XmarteK',
  web: 'xmartek.co',
  color: '1F4E5F',
  acento: '2E74B5',
  fila: 'EAF1F4',
  logo: '/marca/xmartek-logo.png',
  // Medidas del logo (310x63 px) y como va en el encabezado: 1,9 pulgadas de ancho.
  logoAncho: 1737360,
  logoAlto: 353080,
  encabezado: 'Propuesta Técnica y Comercial',
  sedes: [
    'XMARTEK LLC · 2875 NE 191ST, Suite 500, Aventura, FL 33180, USA · +1 305 929 0404',
    'XMARDATA DE COLOMBIA S.A.S. · Av. Cra. 9 No. 115-06/30, Bogotá, Colombia · +57 310 2266858 · xmartek.co',
  ],
};

type Trozo = { texto: string; negrita: boolean; codigo: boolean };
// **negrita** y `codigo` dentro de una linea.
export function trozos(t: string): Trozo[] {
  return t
    .split(/(\*\*[^*]+\*\*|`[^`\n]+`)/g)
    .filter(Boolean)
    .map((x) => (/^\*\*[^*]+\*\*$/.test(x) ? { texto: x.slice(2, -2), negrita: true, codigo: false } : /^`[^`\n]+`$/.test(x) ? { texto: x.slice(1, -1), negrita: false, codigo: true } : { texto: x, negrita: false, codigo: false }));
}

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const fechaLarga = (d: Date) => d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });

// Bloques del documento: el primer titulo es el titulo del documento; los bloques ```archivo``` dentro se aplanan.
function bloquesDoc(md: string): { titulo: string; bloques: BloqueIA[] } {
  const bs = bloquesIA(md).flatMap((b): BloqueIA[] => (b.tipo === 'archivo' ? bloquesIA(b.contenido) : b.tipo === 'adjunto' ? [] : [b]));
  const i = bs.findIndex((b) => b.tipo === 'titulo');
  if (i === 0) return { titulo: (bs[0] as { texto: string }).texto, bloques: bs.slice(1) };
  return { titulo: '', bloques: bs };
}

// Nombre del archivo sin extension, como titulo de respaldo ("propuesta-ai-coopelesca" -> "propuesta ai coopelesca").
export const tituloDeNombre = (nombre: string) => nombre.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();

// ---------- HTML (vista para imprimir) ----------
const htmlLinea = (t: string) => trozos(t).map((x) => (x.negrita ? `<b>${esc(x.texto)}</b>` : x.codigo ? `<code>${esc(x.texto)}</code>` : esc(x.texto))).join('');

export function htmlDocumento(md: string, titulo = '', hoy = new Date(), origen = '') {
  const d = bloquesDoc(md);
  const tit = d.titulo || titulo;
  const cuerpo = d.bloques
    .map((b) => {
      if (b.tipo === 'titulo') return `<h2>${htmlLinea(b.texto)}</h2>`;
      if (b.tipo === 'parrafo') return `<p>${htmlLinea(b.texto)}</p>`;
      if (b.tipo === 'lista') return `<ul>${b.items.map((x) => `<li>${htmlLinea(x)}</li>`).join('')}</ul>`;
      if (b.tipo === 'codigo') return `<pre>${esc(b.texto)}</pre>`;
      if (b.tipo === 'tabla')
        return `<table><thead><tr>${b.encabezado.map((c) => `<th>${htmlLinea(c)}</th>`).join('')}</tr></thead><tbody>${b.filas.map((f) => `<tr>${f.map((c) => `<td>${htmlLinea(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
      return '';
    })
    .join('\n');
  return `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(tit || MARCA.nombre)}</title><style>
@page{size:letter;margin:16mm 16mm 20mm}
body{font:10.5pt/1.5 Arial,Helvetica,sans-serif;color:#1d1d1f;margin:0}
.cab{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #${MARCA.color};padding-bottom:8px;margin-bottom:18px}
.cab img{height:38px}.cab div{text-align:right;font-size:9pt;color:#${MARCA.color}}.cab b{display:block;font-size:10pt}
h1{font-size:17pt;color:#${MARCA.color};margin:0 0 14px}h2{font-size:12.5pt;color:#${MARCA.color};margin:18px 0 6px}
p{margin:0 0 8px}ul{margin:0 0 10px 20px;padding:0}li{margin:2px 0}
table{border-collapse:collapse;width:100%;margin:6px 0 12px;font-size:9.5pt}th{background:#${MARCA.color};color:#fff;text-align:left}th,td{border:1px solid #c9d3dd;padding:5px 8px;vertical-align:top}
tr:nth-child(even) td{background:#${MARCA.fila}}code,pre{font:9pt Consolas,monospace}pre{white-space:pre-wrap;background:#${MARCA.fila};padding:8px}
.pie{margin-top:28px;border-top:1px solid #c9d3dd;padding-top:6px;font-size:7.5pt;color:#666;text-align:center;line-height:1.4}
</style></head><body>
<div class="cab"><img src="${origen}${MARCA.logo}" alt="XmarteK"><div><b>${MARCA.encabezado}</b>${esc(fechaLarga(hoy))}</div></div>
${tit ? `<h1>${htmlLinea(tit)}</h1>` : ''}
${cuerpo}
<div class="pie">${MARCA.sedes.map(esc).join('<br>')}</div>
</body></html>`;
}

// Abre el documento en una ventana y pide imprimir (respaldo cuando el servidor no hace el PDF).
export function imprimirDocumento(md: string, titulo = ''): boolean {
  const w = window.open('', '_blank', 'width=900,height=900');
  if (!w) return false;
  w.document.write(htmlDocumento(md, titulo, new Date(), location.origin));
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 600);
  return true;
}

// ---------- Word (.docx) ----------
const xesc = (t: string) => esc(t).replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');
function runs(t: string, extra = '') {
  return trozos(t)
    .map((x) => `<w:r><w:rPr>${extra}${x.negrita ? '<w:b/>' : ''}${x.codigo ? '<w:rFonts w:ascii="Consolas" w:hAnsi="Consolas"/>' : ''}</w:rPr><w:t xml:space="preserve">${xesc(x.texto)}</w:t></w:r>`)
    .join('');
}
const parrafo = (t: string, estilo = '', ppr = '') => `<w:p><w:pPr>${estilo ? `<w:pStyle w:val="${estilo}"/>` : ''}${ppr}</w:pPr>${runs(t)}</w:p>`;

// Ancho de cada columna segun lo que trae (antes todas iguales: "#" quedaba ancha y "Actividad" apretada). Cada columna
// pesa su texto mas largo, entre 4 y 40 caracteres, y nunca baja de 700 (1,2 cm).
export function anchosColumnas(encabezado: string[], filas: string[][], total = 9360) {
  const n = Math.max(1, encabezado.length, ...filas.map((f) => f.length));
  const largo = (t = '') => t.replace(/\*\*|`/g, '').length;
  const pesos = Array.from({ length: n }, (_, i) => Math.min(40, Math.max(4, largo(encabezado[i]), ...filas.map((f) => largo(f[i])))));
  const suma = pesos.reduce((a, b) => a + b, 0);
  const anchos = pesos.map((p) => Math.max(700, Math.floor((p / suma) * total)));
  const sobra = anchos.reduce((a, b) => a + b, 0) - total;
  // Lo que sobra o falta por redondeo se ajusta en la columna mas ancha.
  if (sobra !== 0) anchos[anchos.indexOf(Math.max(...anchos))] -= sobra;
  return anchos;
}

function tablaDocx(encabezado: string[], filas: string[][]) {
  const n = Math.max(encabezado.length, ...filas.map((f) => f.length));
  const anchos = anchosColumnas(encabezado, filas);
  const celda = (t: string, cab: boolean, par: boolean, ancho: number) =>
    `<w:tc><w:tcPr><w:tcW w:w="${ancho}" w:type="dxa"/>${cab ? `<w:shd w:val="clear" w:color="auto" w:fill="${MARCA.color}"/>` : par ? `<w:shd w:val="clear" w:color="auto" w:fill="${MARCA.fila}"/>` : ''}</w:tcPr><w:p><w:pPr><w:spacing w:before="40" w:after="40"/></w:pPr>${runs(t, cab ? '<w:b/><w:color w:val="FFFFFF"/>' : '')}</w:p></w:tc>`;
  const fila = (f: string[], cab: boolean, par: boolean) => `<w:tr>${cab ? '<w:trPr><w:tblHeader/></w:trPr>' : ''}${Array.from({ length: n }, (_, i) => celda(f[i] ?? '', cab, par, anchos[i])).join('')}</w:tr>`;
  const borde = (l: string) => `<w:${l} w:val="single" w:sz="4" w:space="0" w:color="C9D3DD"/>`;
  return `<w:tbl><w:tblPr><w:tblW w:w="5000" w:type="pct"/><w:tblLayout w:type="fixed"/><w:tblBorders>${['top', 'left', 'bottom', 'right', 'insideH', 'insideV'].map(borde).join('')}</w:tblBorders><w:tblCellMar><w:left w:w="100" w:type="dxa"/><w:right w:w="100" w:type="dxa"/></w:tblCellMar></w:tblPr><w:tblGrid>${anchos.map((a) => `<w:gridCol w:w="${a}"/>`).join('')}</w:tblGrid>${fila(encabezado, true, false)}${filas.map((f, i) => fila(f, false, i % 2 === 1)).join('')}</w:tbl><w:p/>`;
}

export function xmlDocumento(md: string, titulo = '') {
  const d = bloquesDoc(md);
  const tit = d.titulo || titulo;
  const cuerpo = d.bloques
    .map((b) => {
      if (b.tipo === 'titulo') return parrafo(b.texto, 'Heading1');
      if (b.tipo === 'parrafo') return parrafo(b.texto);
      if (b.tipo === 'lista') return b.items.map((x) => parrafo(`•  ${x}`, '', '<w:ind w:left="360" w:hanging="220"/><w:spacing w:after="40"/>')).join('');
      if (b.tipo === 'codigo') return b.texto.split('\n').map((x) => `<w:p><w:pPr><w:spacing w:after="0"/></w:pPr>${runs(`\`${x || ' '}\``)}</w:p>`).join('');
      if (b.tipo === 'tabla') return tablaDocx(b.encabezado, b.filas);
      return '';
    })
    .join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><w:body>${tit ? parrafo(tit, 'Title') : ''}${cuerpo}<w:sectPr><w:headerReference w:type="default" r:id="rIdCab"/><w:footerReference w:type="default" r:id="rIdPie"/><w:pgSz w:w="12240" w:h="15840"/><w:pgMar w:top="1500" w:right="1200" w:bottom="1400" w:left="1200" w:header="500" w:footer="500" w:gutter="0"/></w:sectPr></w:body></w:document>`;
}

const NS_W = 'xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"';
const NS_R = 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"';
const NS_WP = 'xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"';
const REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

// Logo en linea dentro del encabezado (a:blip apunta a word/media/logo.png por rIdLogo).
const dibujoLogo = () =>
  `<w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent cx="${MARCA.logoAncho}" cy="${MARCA.logoAlto}"/><wp:docPr id="1" name="Logo XmarteK"/><a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:nvPicPr><pic:cNvPr id="0" name="logo.png"/><pic:cNvPicPr/></pic:nvPicPr><pic:blipFill><a:blip r:embed="rIdLogo"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill><pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${MARCA.logoAncho}" cy="${MARCA.logoAlto}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r>`;

function partesDocx(md: string, titulo: string, hoy: Date, logo: Uint8Array | null): [string, string | Uint8Array][] {
  const gris = (t: string, tam = 16) => `<w:r><w:rPr><w:color w:val="666666"/><w:sz w:val="${tam}"/></w:rPr><w:t xml:space="preserve">${xesc(t)}</w:t></w:r>`;
  const marcaTexto = `<w:r><w:rPr><w:b/><w:color w:val="${MARCA.color}"/><w:sz w:val="36"/></w:rPr><w:t>${MARCA.nombre}</w:t></w:r>`;
  const cabecera = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:hdr ${NS_W} ${NS_R} ${NS_WP}><w:p><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="12" w:space="6" w:color="${MARCA.color}"/></w:pBdr><w:tabs><w:tab w:val="right" w:pos="9840"/></w:tabs><w:spacing w:after="0"/></w:pPr>${logo ? dibujoLogo() : marcaTexto}<w:r><w:tab/></w:r><w:r><w:rPr><w:b/><w:color w:val="${MARCA.color}"/><w:sz w:val="18"/></w:rPr><w:t>${xesc(MARCA.encabezado)}</w:t></w:r><w:r><w:br/></w:r><w:r><w:tab/></w:r>${gris(fechaLarga(hoy))}</w:p></w:hdr>`;
  const campo = (instr: string) =>
    `<w:r><w:rPr><w:color w:val="666666"/><w:sz w:val="14"/></w:rPr><w:fldChar w:fldCharType="begin"/></w:r><w:r><w:rPr><w:color w:val="666666"/><w:sz w:val="14"/></w:rPr><w:instrText xml:space="preserve"> ${instr} </w:instrText></w:r><w:r><w:rPr><w:color w:val="666666"/><w:sz w:val="14"/></w:rPr><w:fldChar w:fldCharType="end"/></w:r>`;
  const pie = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:ftr ${NS_W}><w:p><w:pPr><w:pBdr><w:top w:val="single" w:sz="4" w:space="4" w:color="C9D3DD"/></w:pBdr><w:jc w:val="center"/><w:spacing w:after="0"/></w:pPr>${gris(MARCA.sedes[0], 14)}</w:p><w:p><w:pPr><w:jc w:val="center"/><w:spacing w:after="0"/></w:pPr>${gris(MARCA.sedes[1], 14)}</w:p><w:p><w:pPr><w:jc w:val="right"/><w:spacing w:after="0"/></w:pPr>${gris('Página ', 14)}${campo('PAGE')}${gris(' de ', 14)}${campo('NUMPAGES')}</w:p></w:ftr>`;
  const estilos = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles ${NS_W}><w:docDefaults><w:rPrDefault><w:rPr><w:rFonts w:ascii="Arial" w:hAnsi="Arial" w:eastAsia="Arial" w:cs="Arial"/><w:sz w:val="21"/><w:szCs w:val="21"/><w:lang w:val="es-CO"/></w:rPr></w:rPrDefault><w:pPrDefault><w:pPr><w:spacing w:after="120" w:line="276" w:lineRule="auto"/></w:pPr></w:pPrDefault></w:docDefaults>
<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/></w:style>
<w:style w:type="paragraph" w:styleId="Title"><w:name w:val="Title"/><w:basedOn w:val="Normal"/><w:pPr><w:spacing w:after="240"/></w:pPr><w:rPr><w:b/><w:color w:val="${MARCA.color}"/><w:sz w:val="34"/></w:rPr></w:style>
<w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:basedOn w:val="Normal"/><w:pPr><w:keepNext/><w:spacing w:before="280" w:after="100"/><w:outlineLvl w:val="0"/></w:pPr><w:rPr><w:b/><w:color w:val="${MARCA.color}"/><w:sz w:val="25"/></w:rPr></w:style>
</w:styles>`;
  const partes: [string, string | Uint8Array][] = [
    ['[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Default Extension="png" ContentType="image/png"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/><Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/><Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/><Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/></Types>`],
    ['_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="${REL}/officeDocument" Target="word/document.xml"/></Relationships>`],
    ['word/_rels/document.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rIdEst" Type="${REL}/styles" Target="styles.xml"/><Relationship Id="rIdCab" Type="${REL}/header" Target="header1.xml"/><Relationship Id="rIdPie" Type="${REL}/footer" Target="footer1.xml"/></Relationships>`],
    ['word/document.xml', xmlDocumento(md, titulo)],
    ['word/styles.xml', estilos],
    ['word/header1.xml', cabecera],
    ['word/footer1.xml', pie],
  ];
  if (logo) {
    partes.push(['word/_rels/header1.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rIdLogo" Type="${REL}/image" Target="media/logo.png"/></Relationships>`]);
    partes.push(['word/media/logo.png', logo]);
  }
  return partes;
}

export const docxDocumento = (md: string, titulo = '', hoy = new Date(), logo: Uint8Array | null = null) => zip(partesDocx(md, titulo, hoy, logo));

// El logo se trae una vez de la Consola (public/marca). Si no carga, el encabezado lleva el nombre en texto.
let logoCache: Promise<Uint8Array | null> | null = null;
export const cargarLogo = () =>
  (logoCache ??= fetch(MARCA.logo)
    .then((r) => (r.ok ? r.arrayBuffer() : null))
    .then((b) => (b ? new Uint8Array(b) : null))
    .catch(() => null));

export const TIPO_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const nombreConExt = (nombre: string, ext: string) => `${nombre.replace(/\.[^.]+$/, '')}.${ext}`;

// El .docx como archivo (para descargar o adjuntar a una respuesta).
export async function archivoWord(nombre: string, md: string) {
  return new File([docxDocumento(md, tituloDeNombre(nombre), new Date(), await cargarLogo())], nombreConExt(nombre, 'docx'), { type: TIPO_DOCX });
}

export function bajarArchivo(f: Blob, nombre: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(f);
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

export async function descargarWord(nombre: string, md: string) {
  const f = await archivoWord(nombre, md);
  bajarArchivo(f, f.name);
}
